import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './styles/App.css';
import './styles/LangSwitcher.css';
import { SITE_DATA } from './data';
import mapImage from './assets/map.png';
import helmetImage from './assets/casco.png';

function App() {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentBg, setCurrentBg] = useState(0);
  const [selectedImg, setSelectedImg] = useState(null);
  const [showTourDetail, setShowTourDetail] = useState(null);
  
  const [bookingBike, setBookingBike] = useState(null);
  const [showSpecs, setShowSpecs] = useState(null);
  const [showPricing, setShowPricing] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [bikeSize, setBikeSize] = useState("M");
  const [duration, setDuration] = useState("full"); // "full" o "half"
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // Stati per il modulo di contatto
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMsg, setContactMsg] = useState("");
  const [isContactSent, setIsContactSent] = useState(false);
  const [isContactSending, setIsContactSending] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % SITE_DATA.hero.bgImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const openLightbox = (imgSrc) => {
    setSelectedImg(imgSrc);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImg(null);
    document.body.style.overflow = 'auto';
  };

  const openBooking = (bike) => {
    setBookingBike(bike);
    setIsSent(false);
    setDuration("full");
    document.body.style.overflow = 'hidden';
  };

  const closeBooking = () => {
    setBookingBike(null);
    setStartDate("");
    setEndDate("");
    setCustomerEmail("");
    setCustomerName("");
    setDuration("full");
    setIsSent(false);
    document.body.style.overflow = 'auto';
  };

  const openSpecs = (bike) => {
    setShowSpecs(bike);
    document.body.style.overflow = 'hidden';
  };

  const closeSpecs = () => {
    setShowSpecs(null);
    document.body.style.overflow = 'auto';
  };

  const openPricing = (bike) => {
    setShowPricing(bike);
    document.body.style.overflow = 'hidden';
  };

  const closePricing = () => {
    setShowPricing(null);
    document.body.style.overflow = 'auto';
  };

  const calculateTotal = () => {
    if (!startDate) return 0;
    
    if (duration === "half") {
      return t('booking.seeRates');
    }

    if (!endDate) return bookingBike.pricing.daily.price;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; 

    if (diffDays === 1) return bookingBike.pricing.daily.price;
    if (diffDays === 2) return bookingBike.pricing.twoDays.price;
    if (diffDays === 3) return bookingBike.pricing.threeDays.price;
    if (diffDays >= 4 && diffDays <= 5) return bookingBike.pricing.fiveDays.price;
    if (diffDays >= 6 && diffDays <= 7) return bookingBike.pricing.sevenDays.price;
    if (diffDays > 7 && diffDays <= 30) return bookingBike.pricing.monthly.price;
    
    return t('booking.contactForQuote');
  };

  const sendEmail = async () => {
    setIsSending(true);
    const total = calculateTotal();
    const formData = {
      Nome_Richiedente: customerName,
      Bici: bookingBike.name,
      Taglia: bikeSize,
      Durata: duration === "full" ? t('booking.fullDay') : t('booking.halfDay'),
      Dal: startDate,
      Al: duration === "full" ? endDate : "N/A",
      Prezzo_Stimato: total,
      Email_Cliente: customerEmail,
      _subject: `Nuova Richiesta Noleggio: ${bookingBike.name} - ${customerName}`,
      _honey: "" 
    };

    try {
      const response = await fetch("https://formsubmit.co/ajax/bikeirpinia@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsSent(true);
      } else {
        throw new Error();
      }
    } catch (error) {
      alert(t('contacts.errorMsg') || "C'è stato un problema nell'invio della richiesta. Riprova più tardi.");
    } finally {
      setIsSending(false);
    }
  };

  const sendContactEmail = async (e) => {
    e.preventDefault();
    setIsContactSending(true);
    const formData = {
      Nome: contactName,
      Email: contactEmail,
      Messaggio: contactMsg,
      _subject: `Nuovo Messaggio da Sito: ${contactName}`,
      _honey: ""
    };

    try {
      const response = await fetch("https://formsubmit.co/ajax/bikeirpinia@gmail.com", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setIsContactSent(true);
        setContactName("");
        setContactEmail("");
        setContactMsg("");
        setTimeout(() => setIsContactSent(false), 5000);
      } else {
        throw new Error();
      }
    } catch (error) {
      alert(t('contacts.errorMsg') || "C'è stato un problema nell'invio del messaggio. Riprova più tardi.");
    } finally {
      setIsContactSending(false);
    }
  };

  const sendWhatsApp = () => {
    const total = calculateTotal();
    const durLabel = duration === "full" ? t('booking.fullDay') : t('booking.halfDay');
    const dateInfo = duration === "full" ? `📅 ${t('booking.from')} ${startDate} ${t('booking.to')} ${endDate}` : `📅 ${t('booking.day')} ${startDate}`;
    const text = encodeURIComponent(
      `*RICHIESTA NOLEGGIO*\n` +
      `👤 Nome Richiedente: ${customerName}\n` +
      `🚲 Bici: ${bookingBike.name}\n` +
      `📏 Taglia: ${bikeSize}\n` +
      `⏱️ Durata: ${durLabel}\n` +
      `${dateInfo}\n` +
      `💰 ${t('booking.estimatedPrice')} ${total}\n` +
      `📧 Email: ${customerEmail}`
    );
    window.open(`https://wa.me/393273183597?text=${text}`, '_blank');
  };

  const isContainBg = SITE_DATA.hero.bgImages[currentBg].includes('LOGO') || SITE_DATA.hero.bgImages[currentBg].includes('LAPIO 3');

  return (
    <div className="App" style={{ overflowX: 'hidden', width: '100%' }}>
      <div className="top-banner">
        {t('hero.banner')}
      </div>
      
      <nav className="navbar" style={{ top: '40px', position: 'fixed', width: '100%', zIndex: 1000 }}>
        <div className="logo-container">
          <img src={SITE_DATA.logo} alt="Logo" className="logo-img" />
          <div className="logo-text">Irpinia Bike <span>LAPIO</span></div>
        </div>

        {/* HAMBURGER FORZATO */}
        <div 
          onClick={toggleMenu}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            cursor: 'pointer',
            zIndex: 3000,
            padding: '10px',
            marginRight: '15px'
          }}
        >
          <span style={{ width: '30px', height: '3px', background: '#1b5e20', transition: '0.3s', transform: isMenuOpen ? 'translateY(9px) rotate(45deg)' : 'none' }}></span>
          <span style={{ width: '30px', height: '3px', background: '#1b5e20', transition: '0.3s', opacity: isMenuOpen ? 0 : 1 }}></span>
          <span style={{ width: '30px', height: '3px', background: '#1b5e20', transition: '0.3s', transform: isMenuOpen ? 'translateY(-9px) rotate(-45deg)' : 'none' }}></span>
        </div>

        <ul 
          style={{
            position: 'fixed',
            top: 0,
            right: isMenuOpen ? '0' : '-100%',
            width: '100%',
            height: '100vh',
            background: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '2rem',
            transition: '0.4s ease-in-out',
            zIndex: 2500,
            listStyle: 'none',
            margin: 0,
            padding: 0,
            boxShadow: '-5px 0 15px rgba(0,0,0,0.1)',
            visibility: isMenuOpen ? 'visible' : 'hidden'
          }}
        >
          <li><a href="#home" style={{ fontSize: '1.8rem', fontWeight: '700', textDecoration: 'none', color: '#1a1a1a', textTransform: 'uppercase' }} onClick={toggleMenu}>{t('nav.home')}</a></li>
          <li><a href="#chi-siamo" style={{ fontSize: '1.8rem', fontWeight: '700', textDecoration: 'none', color: '#1a1a1a', textTransform: 'uppercase' }} onClick={toggleMenu}>{t('nav.about')}</a></li>
          <li><a href="#percorsi" style={{ fontSize: '1.8rem', fontWeight: '700', textDecoration: 'none', color: '#1a1a1a', textTransform: 'uppercase' }} onClick={toggleMenu}>{t('nav.tours')}</a></li>
          <li><a href="#bici" style={{ fontSize: '1.8rem', fontWeight: '700', textDecoration: 'none', color: '#1a1a1a', textTransform: 'uppercase' }} onClick={toggleMenu}>{t('nav.bikes')}</a></li>
          <li><a href="#gallery" style={{ fontSize: '1.8rem', fontWeight: '700', textDecoration: 'none', color: '#1a1a1a', textTransform: 'uppercase' }} onClick={toggleMenu}>{t('nav.gallery')}</a></li>
          <li><a href="#contatti" style={{ fontSize: '1.8rem', fontWeight: '700', textDecoration: 'none', color: '#1a1a1a', textTransform: 'uppercase' }} onClick={toggleMenu}>{t('nav.contacts')}</a></li>
          
          <li className="menu-lang-container" style={{ marginTop: '2rem', borderTop: '1px solid #eee', paddingTop: '2rem', width: '80%', display: 'flex', justifyContent: 'center' }}>
            <div className="lang-switcher">
              {['it', 'en', 'de', 'fr', 'es'].map((lang) => (
                <button
                  key={lang}
                  className={`lang-btn ${i18n.language === lang ? 'active' : ''}`}
                  onClick={() => {
                    i18n.changeLanguage(lang);
                    toggleMenu();
                  }}
                  style={{ fontSize: '1.2rem', padding: '10px 15px' }}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
          </li>
        </ul>
      </nav>

      <section 
        id="home" 
        className="hero" 
        style={{ 
          height: '100vh', 
          backgroundSize: isContainBg ? 'contain' : 'cover', 
          backgroundRepeat: 'no-repeat',
          backgroundColor: isContainBg ? '#fff' : '#000',
          backgroundPosition: 'center center', 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.1), rgba(0,0,0,0.3)), url('${SITE_DATA.hero.bgImages[currentBg]}')`, 
          display: 'flex', 
          alignItems: 'flex-end', 
          justifyContent: 'flex-end',
          padding: '0 5% 5% 0'
        }}
      >
        <div 
          className="hero-box" 
          style={{ 
            textAlign: 'left', 
            background: 'rgba(255,255,255,0.85)', 
            padding: '1.5rem 2rem', 
            borderRadius: '15px', 
            maxWidth: '350px', 
            borderLeft: '8px solid #1b5e20',
            boxShadow: '0 5px 20px rgba(0,0,0,0.15)',
            backdropFilter: 'blur(5px)'
          }}
        >
          <h1 style={{ fontSize: '1.6rem', fontWeight: '800', marginBottom: '1.2rem', color: '#1a1a1a' }}>{t('hero.title')}</h1>
          <a href="#bici" className="cta-button" style={{ padding: '0.8rem 1.5rem', fontSize: '1rem' }}>{t('hero.cta')}</a>
        </div>
      </section>

      <section id="chi-siamo" className="section">
        <h2 className="section-title">{t('about.title')}</h2>
        <div className="container-small">
          <p style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>
            {t('about.description').split(/(\*\*.*?\*\*)/g).map((part, i) => 
              part.startsWith('**') && part.endsWith('**') 
                ? <strong key={i}>{part.slice(2, -2)}</strong> 
                : part
            )}
          </p>
        </div>
      </section>

      <section id="percorsi" className="section">
        <h2 className="section-title">{t('tours.title')}</h2>
        <div className="tours-container">
          {SITE_DATA.tours.map((tour, idx) => (
            <div key={idx} className="destinazione-card full-width" style={{ marginBottom: '2rem' }}>
              <div className="destinazione-img" onClick={() => openLightbox(tour.image)} style={{ position: 'relative' }}>
                <img src={tour.image} alt={t(`tours.tour${idx + 1}.title`)} />
                {tour.price && <div className="price-tag">€{tour.price}</div>}
                {tour.overlayText && (
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '20px',
                    color: 'white',
                    fontSize: '1.5rem',
                    fontWeight: '800',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.7)',
                    zIndex: 10
                  }}>
                    {tour.overlayText}
                  </div>
                )}
                <div className="zoom-overlay">{t('tours.zoom')}</div>
              </div>
              <div className="destinazione-info">
                <h3>{t(`tours.tour${idx + 1}.title`)}</h3>
                <p>{t(`tours.tour${idx + 1}.description`)}</p>
                <ul className="docg-list" style={{ marginBottom: '2rem' }}>
                  {tour.vini.map((vino, vIdx) => (
                    <li key={vIdx}>{vino.icon} <strong>{t(`tours.tour${idx + 1}.vini.${vIdx}.nome`)}:</strong> {t(`tours.tour${idx + 1}.vini.${vIdx}.docg`)}</li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => {
                    if (tour.itinerario) {
                      setShowTourDetail({ ...tour, tourId: idx + 1 });
                      document.body.style.overflow = 'hidden';
                    } else if (tour.link.startsWith('#')) {
                      window.location.href = tour.link;
                    } else {
                      window.open(tour.link, '_blank');
                    }
                  }}
                  className="cta-button"
                  style={{ width: '100%', textAlign: 'center', border: 'none', cursor: 'pointer' }}
                >
                  {tour.itinerario ? t('tours.button') : t('tours.bookingButton')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="bici" className="section bg-alt">
        <h2 className="section-title">{t('bikes.title')}</h2>
        <div className="grid">
          {SITE_DATA.bikes.map(bike => (
            <div key={bike.id} className="bike-card">
              <div className="bike-img" onClick={() => openLightbox(bike.img)}>
                <img src={bike.img} alt={bike.name} />
                <div className="zoom-overlay">{t('tours.zoom')}</div>
              </div>
              <div className="bike-info">
                <h4>{bike.name}</h4>
                <p>{t(`bikes.bike${bike.id}.desc`)}</p>
                <div className="bike-actions-main">
                  <button className="specs-btn" onClick={() => openSpecs(bike)}>{t('bikes.specs')}</button>
                  <button className="specs-btn" onClick={() => openPricing(bike)}>{t('bikes.pricing')}</button>
                  <button className="book-btn" onClick={() => openBooking(bike)}>{t('bikes.book')}</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="servizi" className="section">
        <h2 className="section-title">{t('services.title')}</h2>
        <div className="grid">
          <div className="service-card" style={{ background: '#f9f9f9', padding: '2rem', borderRadius: '20px', border: '1px solid #eee', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚚</div>
            <h4 style={{ fontSize: '1.4rem', color: '#1b5e20', marginBottom: '1rem' }}>{t('services.delivery.title')}</h4>
            <p style={{ marginBottom: '1.5rem' }}>{t('services.delivery.desc')}</p>
            <div style={{ background: 'white', padding: '1rem', borderRadius: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
              <p style={{ margin: '0.5rem 0' }}>📍 {t('services.delivery.within10')}: <strong>€20</strong></p>
              <p style={{ margin: '0.5rem 0' }}>📍 {t('services.delivery.within20')}: <strong>€35</strong></p>
            </div>
          </div>
          
          <div className="service-card" style={{ background: '#f9f9f9', padding: '2rem', borderRadius: '20px', border: '1px solid #eee', textAlign: 'center' }}>
            <div style={{ marginBottom: '1rem' }}>
              <img src={helmetImage} alt="Casco da ciclista" style={{ width: '100px', height: 'auto', margin: '0 auto', borderRadius: '10px' }} />
            </div>
            <h4 style={{ fontSize: '1.4rem', color: '#1b5e20', marginBottom: '1rem' }}>{t('services.helmet.title')}</h4>
            <p style={{ marginBottom: '1.5rem' }}>{t('services.helmet.desc')}</p>
            <div style={{ background: 'white', padding: '1rem', borderRadius: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
              <p>{t('services.helmet.info')}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="gallery" className="section">
        <h2 className="section-title">{t('gallery.title')}</h2>
        <div className="gallery-grid">
          <div className="gallery-item" onClick={() => openLightbox(mapImage)}>
            <img src={mapImage} alt="Mappa Percorsi" style={{ objectFit: 'contain', background: '#f0f0f0' }} />
            <div className="zoom-overlay">{t('gallery.map')}</div>
          </div>
          {SITE_DATA.galleryImages.map((image, idx) => (
            <div key={idx} className="gallery-item" onClick={() => openLightbox(image.src)}>
              <img src={image.src} alt={image.alt} />
              <div className="zoom-overlay">{t('tours.zoom')}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="contatti" className="section bg-alt" style={{ textAlign: 'center' }}>
        <h2 className="section-title">{t('contacts.title')}</h2>
        <div className="contact-wrapper" style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2.5rem' }}>
          <div className="contact-info" style={{ width: '100%', marginBottom: '1rem' }}>
            <p style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>📍 {SITE_DATA.contatti.indirizzo}</p>
            <p style={{ fontSize: '2rem', fontWeight: '800', color: '#1b5e20', wordBreak: 'break-all' }}>📧 {SITE_DATA.contatti.email}</p>
          </div>
          {isContactSent ? (
            <div className="success-message" style={{ background: '#e8f5e9', padding: '2rem', borderRadius: '15px', width: '100%' }}>
              <h3>{t('contacts.successTitle')}</h3>
              <p>{t('contacts.successDesc')}</p>
            </div>
          ) : (
            <form className="contact-form" onSubmit={sendContactEmail} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
              <input 
                type="text" 
                placeholder={t('contacts.name')}
                required 
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                style={{ padding: '1.2rem', fontSize: '1.3rem', borderRadius: '12px', border: '1px solid #ddd', width: '100%' }} 
              />
              <input 
                type="email" 
                placeholder={t('contacts.email')}
                required 
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                style={{ padding: '1.2rem', fontSize: '1.3rem', borderRadius: '12px', border: '1px solid #ddd', width: '100%' }} 
              />
              <textarea 
                rows="4" 
                placeholder={t('contacts.message')}
                required 
                value={contactMsg}
                onChange={(e) => setContactMsg(e.target.value)}
                style={{ padding: '1.2rem', fontSize: '1.3rem', borderRadius: '12px', border: '1px solid #ddd', width: '100%', resize: 'vertical' }}
              ></textarea>
              <button 
                type="submit" 
                className="cta-button" 
                disabled={isContactSending}
                style={{ padding: '1.2rem', fontSize: '1.3rem', width: '100%', marginTop: '1rem' }}
              >
                {isContactSending ? t('contacts.sending') : t('contacts.send')}
              </button>
            </form>
          )}
        </div>
      </section>

      <footer style={{ padding: '3rem', textAlign: 'center', background: '#f9f9f9', borderTop: '1px solid #eee' }}>
        <p>&copy; 2026 Irpinia Bike LAPIO. {t('footer.copy')}</p>
      </footer>

      {bookingBike && (
        <div className="modal-overlay" onClick={closeBooking}>
          <div className="modal-content small-modal" onClick={(e) => e.stopPropagation()}>
            <span className="close-modal" onClick={closeBooking}>&times;</span>
            
            {isSent ? (
              <div className="success-message">
                <h3>{t('booking.successTitle')}</h3>
                <p>{t('booking.successDesc')} <strong>{customerEmail}</strong>.</p>
                <button className="cta-button" onClick={closeBooking} style={{marginTop: '1rem'}}>{t('booking.close')}</button>
              </div>
            ) : (
              <>
                <h3>{t('booking.title')}</h3>
                <p className="bike-info-mini"><strong>{bookingBike.name}</strong></p>
                
                <div className="booking-form-compact">
                  <div className="form-group-compact">
                    <label>{t('booking.duration')}</label>
                    <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                      <option value="full">{t('booking.fullDay')}</option>
                      <option value="half">{t('booking.halfDay')}</option>
                    </select>
                  </div>

                  <div className="form-group-compact">
                    <label>{t('booking.yourName')}</label>
                    <input type="text" placeholder={t('booking.yourNamePlaceholder')} value={customerName} onChange={(e) => setCustomerName(e.target.value)} required />
                  </div>

                  <div className="form-row">
                    <div className="form-group-compact">
                      <label>{duration === "full" ? t('booking.from') : t('booking.day')}</label>
                      <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                    </div>
                    {duration === "full" && (
                      <div className="form-group-compact">
                        <label>{t('booking.to')}</label>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                      </div>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group-compact" style={{flex: '0.4'}}>
                      <label>{t('booking.size')}</label>
                      <select value={bikeSize} onChange={(e) => setBikeSize(e.target.value)}>
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                      </select>
                    </div>
                    <div className="form-group-compact">
                      <label>{t('booking.yourEmail')}</label>
                      <input type="email" placeholder="tua@email.it" value={customerEmail} onChange={(e) => setCustomerEmail(e.target.value)} required />
                    </div>
                  </div>

                  <div className="booking-total">
                    <span>{t('booking.estimatedPrice')}</span> <strong>{calculateTotal()}</strong>
                  </div>

                  <div className="booking-actions">
                    <button className="send-booking-btn-compact" onClick={sendEmail} disabled={!startDate || (duration === "full" && !endDate) || !customerEmail || !customerName || isSending}>
                      {isSending ? t('booking.sending') : t('booking.sendRequest')}
                    </button>
                  </div>
                  <p style={{fontSize: '0.7rem', color: '#999', marginTop: '10px', textAlign: 'center'}}>{t('booking.confirmationNote')}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showSpecs && (
        <div className="modal-overlay" onClick={closeSpecs}>
          <div className="modal-content specs-modal" onClick={(e) => e.stopPropagation()}>
            <span className="close-modal" onClick={closeSpecs}>&times;</span>
            <div className="specs-container">
              <div className="specs-header">
                <img src={showSpecs.img} alt={showSpecs.name} className="specs-bike-img" />
                <h3>{showSpecs.name}</h3>
              </div>
              <div className="specs-body">
                <div className="specs-description">
                  <h4>{t('bikes.description')}</h4>
                  <p>{t(`bikes.bike${showSpecs.id}.fullDesc`)}</p>
                </div>
                <div className="specs-table">
                  <h4>{t('bikes.techSpecs')}</h4>
                  <ul>
                    {Object.entries(t(`bikes.bike${showSpecs.id}.specs`, { returnObjects: true })).map(([key, value]) => (
                      <li key={key}><strong>{key}:</strong> {value}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <button className="cta-button" onClick={() => { closeSpecs(); openBooking(showSpecs); }} style={{ width: '100%', marginTop: '1.5rem' }}>{t('bikes.bookThis')}</button>
            </div>
          </div>
        </div>
      )}

      {showPricing && (
        <div className="modal-overlay" onClick={closePricing}>
          <div className="modal-content specs-modal" onClick={(e) => e.stopPropagation()}>
            <span className="close-modal" onClick={closePricing}>&times;</span>
            <div className="pricing-container" style={{ padding: '0.5rem' }}>
              <h3 style={{ marginBottom: '1.2rem', borderBottom: '2px solid #1b5e20', paddingBottom: '0.5rem', fontSize: '1.4rem' }}>{t('bikes.rates')} {showPricing.name}</h3>
              
              <div className="mobile-friendly-pricing" style={{ marginBottom: '1.5rem' }}>
                <div style={{ background: '#1b5e20', color: 'white', padding: '10px', borderRadius: '10px 10px 0 0', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                  <span>{t('pricing.duration')}</span>
                  <span>{t('pricing.price')}</span>
                </div>
                
                {[
                  { label: t('pricing.periods.daily'), key: "daily" },
                  { label: t('pricing.periods.twoDays'), key: "twoDays" },
                  { label: t('pricing.periods.threeDays'), key: "threeDays" },
                  { label: t('pricing.periods.fiveDays'), key: "fiveDays" },
                  { label: t('pricing.periods.sevenDays'), key: "sevenDays" },
                  { label: t('pricing.periods.monthly'), key: "monthly" }
                ].map((item, index) => (
                  <div key={item.key} style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '12px 10px', 
                    borderBottom: '1px solid #eee',
                    background: index % 2 === 0 ? '#fff' : '#f9f9f9',
                    fontSize: '1.1rem'
                  }}>
                    <span style={{ color: '#555' }}>{item.label}</span>
                    <div style={{ textAlign: 'right' }}>
                      <strong style={{ display: 'block' }}>{showPricing.pricing[item.key].price}</strong>
                      {showPricing.pricing[item.key].discount !== "0%" && (
                        <small style={{ color: '#d32f2f', fontSize: '0.8rem' }}>{t('pricing.discount')} {showPricing.pricing[item.key].discount}</small>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: '#fff9c4', padding: '1rem', borderRadius: '10px', borderLeft: '5px solid #fbc02d', marginBottom: '1.5rem' }}>
                <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: '1.4' }}>
                  <strong>{t('pricing.notes')}</strong> {showPricing.pricing.notes}
                </p>
              </div>
              <button className="cta-button" onClick={() => { closePricing(); openBooking(showPricing); }} style={{ width: '100%', padding: '1rem', fontSize: '1.1rem' }}>{t('bikes.bookNow')}</button>
            </div>
          </div>
        </div>
      )}

      {showTourDetail && (
        <div className="modal-overlay" onClick={() => { setShowTourDetail(null); document.body.style.overflow = 'auto'; }}>
          <div className="modal-content tour-modal" onClick={(e) => e.stopPropagation()}>
            <span className="close-modal" onClick={() => { setShowTourDetail(null); document.body.style.overflow = 'auto'; }}>&times;</span>
            <div className="tour-detail-container">
              <img src={showTourDetail.image} alt={t(`tours.tour${showTourDetail.tourId}.title`)} className="tour-modal-img" />
              <h3>{t(`tours.tour${showTourDetail.tourId}.title`)}</h3>
              <p className="tour-modal-desc">{t(`tours.tour${showTourDetail.tourId}.description`)}</p>
              
              <div className="itinerary-list">
                {t(`tours.tour${showTourDetail.tourId}.itinerary`, { returnObjects: true }).map((tappa, idx) => (
                  <div key={idx} className="itinerary-step">
                    <h4>📍 {tappa.borgo}</h4>
                    <ul>
                      {tappa.punti.map((punto, pIdx) => (
                        <li key={pIdx}>{punto}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                <button className="cta-button" onClick={() => { setShowTourDetail(null); document.body.style.overflow = 'auto'; window.location.href = '#contatti'; }}>{t('tours.requestInfo')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedImg && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <span className="close-lightbox" onClick={closeLightbox}>&times;</span>
            <img src={selectedImg} alt="Ingrandimento" className="lightbox-img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
